import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useGoogleLogin } from '@react-oauth/google';
import { FcGoogle } from "react-icons/fc";
import { FiEye, FiEyeOff, FiShield, FiX } from "react-icons/fi";

import BacktoHome from "../../../components/btns/BacktoHome";
import { useSignin } from "../../../hooks/auth/useSignin";
import { useGoogleAuth } from "../../../hooks/auth/useGoogleAuth";
import { validateEmail, validateLoginPassword } from "../../../utils/authValidation";

import SigninImg from "../../../Assets/Images/signin.jpg";
import Logo from "../../../Assets/pngs/logo-white.png";

const SignIn = () => {
  const { signin, loading: manualLoading, error: manualError } = useSignin();
  const { loading: googleLoading, error: googleError } = useGoogleAuth();

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);


  const loginWithGoogle = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: "https://www.aliveparis.com/signin",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 1. Validate inputs before sending to server
    const emailErr = validateEmail(form.email);
    const passErr = validateLoginPassword(form.password);

    if (emailErr || passErr) {
      setErrors({ email: emailErr, password: passErr });
      return; // Stop the request
    }
    await signin(form);
  };

  const handleGoogleAccept = () => {
    setShowPrivacyModal(false);
    loginWithGoogle();
  };

  // 2. Combined loading state
  const isProcessing = manualLoading || googleLoading;

  return (
    <div className="relative min-h-screen bg-white flex items-center justify-center px-4">
      {showPrivacyModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 relative">

            <button
              onClick={() => setShowPrivacyModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <FiX size={18} />
            </button>

            <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <FiShield size={20} className="text-gray-600" />
            </div>

            <h2 className="text-base font-semibold text-gray-900 mb-2">Before you continue</h2>
            <p className="text-sm text-gray-500 leading-relaxed mb-4">
              By signing in with Google, you agree to our{" "}
              <Link to="/privacy-policy" className="text-gray-900 font-medium underline">
                Privacy Policy
              </Link>
              . We use your information only to personalise your experience on Alive Paris.
            </p>

            <div className="bg-gray-50 rounded-lg px-3 py-2.5 text-xs text-gray-500 leading-relaxed mb-5">
              We never sell your data or share it with third parties without your consent.
            </div>

            <button
              onClick={handleGoogleAccept}
              className="flex items-center justify-center gap-2.5 w-full border border-gray-300 rounded-xl py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50 transition mb-2"
            >
              <FcGoogle size={18} />
              Continue with Google
            </button>

            <button
              onClick={() => setShowPrivacyModal(false)}
              className="w-full text-sm text-gray-400 hover:text-gray-600 py-2 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {isProcessing && (
        <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4">
          {/* Spinner */}
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-black animate-spin" />
          <p className="text-sm text-gray-500">
            {googleLoading ? "Signing in with Google..." : "Signing in..."}
          </p>
        </div>
      )}
      <div className="absolute top-4 right-5">
        <BacktoHome />
      </div>

      <div className="w-full max-w-5xl mx-auto flex  overflow-hidden md:bg-stone-100 md:p-2 md:rounded-[2rem]">
        {/* LEFT PANEL */}
        <div className="hidden relative w-full md:flex  items-center justify-center rounded-l-3xl"
          style={{ backgroundImage: `url(${SigninImg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        >
          <div className="absolute bottom-0 left-0  flex flex-col  text-white p-3 rounded-b-3xl bg-gradient-to-t from-black via-black/80 to-transparent p-5">
            <h2 className="text-2xl mb-2">Welcome Back!</h2>
            <p className="text-sm">
              our next great chapter starts here. Unlock your stay and live the Paris you’ve always dreamed of.
            </p>
          </div>
          <div className="absolute top-5 left-5">
            <img src={Logo} alt="logo" className="w-7" />
          </div>
        </div>


        {/* RIGHT PANEL */}
        <div className="w-full bg-white flex items-center justify-center md:rounded-r-3xl">
          <div className="w-full max-w-md px-6 py-10">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-gray-900">Sign in</h1>
              <p className="mt-1 text-sm text-gray-500">
                Sign in to explore available properties and secure your stay today.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>

                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Email"
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-black outline-none"
                  required
                />
                {errors.email && <p className="text-[10px] text-red-500 ml-1">{errors.email}</p>}
              </div>
              <div className="relative">
                <div>

                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-black outline-none"
                    required
                  />
                  {errors.password && <p className="text-[10px] text-red-500 ml-1">{errors.password}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>

              <div className="flex justify-end">
                <Link to='/forgot-password' className="text-sm text-black hover:underline">
                  Forgot Password?
                </Link>
              </div>

              {/* ERROR DISPLAY */}
              {(manualError || googleError) && (
                <p className="text-red-600 text-sm bg-red-50 p-2 rounded">{manualError || googleError}</p>
              )}

              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-black text-white py-2.5 rounded-lg font-medium hover:bg-gray-800 transition disabled:opacity-50"
              >
                {manualLoading ? "Signing in..." : "Sign in"}
              </button>

              <div className="flex items-center my-4">
                <div className="flex-grow h-px bg-gray-200" />
                <span className="mx-3 text-xs font-semibold text-gray-400">OR</span>
                <div className="flex-grow h-px bg-gray-200" />
              </div>

              {/* GOOGLE LOGIN BUTTON */}
              <button
                type="button"
                onClick={() => setShowPrivacyModal(true)}
                disabled={isProcessing}
                className="flex gap-3 items-center justify-center border p-2 w-full rounded-xl"
              >
                <FcGoogle /> {googleLoading ? "Processing..." : "Sign in with Google"}
              </button>
            </form>
            <div>

              <p className="mt-6 text-xs text-gray-500 text-center">
                By signing in, you agree to our{" "}

                <Link to="/privacy-policy" className="font-semibold text-gray-900 hover:underline">
                  Privacy Policy
                </Link>
              </p>
              <p className="mt-2 text-xs text-gray-500 text-center">
                New to Alive Paris?{" "}
                <Link to="/signup" className="font-semibold text-gray-900 hover:underline">
                  Create an account
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;